import React, { useState, useEffect, KeyboardEvent, useRef } from 'react';
import { Tree, TreeNode } from 'react-organizational-chart';
import styled from 'styled-components';
import { API } from '../../../../common/Constants';
import { getAPIHeaders } from '../../../../common/Utils';
import useFetch from '../../../../hooks/useFetch';

interface RelationshipVisualizationProps {
  curriculumId?: number;
  courseId?: number;
  showFullHierarchy?: boolean;
  data?: any;
}

// Styled components for the tree nodes
const StyledNode = styled.div<{ nodeType: string; isFocused?: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  text-align: center;
  min-width: 160px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  outline: ${({ isFocused }) => (isFocused ? '3px solid #1a73e8' : 'none')};
  outline-offset: 2px;

  ${({ nodeType }) => {
    switch (nodeType) {
      case 'curriculum':
        return `
          background-color: rgba(0, 123, 255, 0.15);
          border: 2px solid #007bff;
          color: #007bff;
        `;
      case 'course':
        return `
          background-color: rgba(40, 167, 69, 0.15);
          border: 2px solid #28a745;
          color: #28a745;
        `;
      case 'subject':
        return `
          background-color: rgba(23, 162, 184, 0.15);
          border: 2px solid #17a2b8;
          color: #17a2b8;
        `;
      case 'syllabus':
        return `
          background-color: rgba(128, 0, 128, 0.15);
          border: 2px solid #800080;
          color: #800080;
        `;
      default:
        return `
          background-color: #f8f9fa;
          border: 2px solid #adb5bd;
          color: #495057;
        `;
    }
  }}

  &:hover,
  &:focus {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 3px solid #1a73e8;
    outline-offset: 2px;
  }

  .node-icon {
    margin-right: 8px;
    font-size: 14px;
  }

  .node-title {
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
  }

  .node-subtitle {
    font-size: 11px;
    opacity: 0.8;
    margin-top: 2px;
  }
`;

const RelationshipContainer = styled.div`
  padding: 20px;
  overflow: auto;

  .oc-tree .oc-hierarchy {
    padding: 20px 0;
  }

  .is-leaf {
    margin-top: 10px;
  }

  .visuallyhidden {
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  }
`;

const EmptyStateMessage = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #6c757d;

  i {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h5 {
    font-weight: 500;
    margin-bottom: 8px;
  }
`;

// Node components for each entity type
const CurriculumNode = React.forwardRef<
  HTMLDivElement,
  {
    curriculum: any;
    onClick?: () => void;
    onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
    tabIndex?: number;
    isFocused?: boolean;
    ariaLabel?: string;
  }
>(({ curriculum, onClick, onKeyDown, tabIndex = 0, isFocused, ariaLabel }, ref) => (
  <StyledNode
    nodeType="curriculum"
    onClick={onClick}
    onKeyDown={onKeyDown}
    tabIndex={tabIndex}
    isFocused={isFocused}
    role="button"
    ref={ref}
    aria-label={ariaLabel || `Curriculum: ${curriculum.title}${curriculum.code ? ', Code: ' + curriculum.code : ''}`}
  >
    <div>
      <div className="d-flex align-items-center justify-content-center">
        <i className="ti ti-book node-icon" aria-hidden="true"></i>
        <span className="node-title">{curriculum.title}</span>
      </div>
      {curriculum.code && <div className="node-subtitle">Code: {curriculum.code}</div>}
    </div>
  </StyledNode>
));

const CourseNode = React.forwardRef<
  HTMLDivElement,
  {
    course: any;
    onClick?: () => void;
    onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
    tabIndex?: number;
    isFocused?: boolean;
    ariaLabel?: string;
  }
>(({ course, onClick, onKeyDown, tabIndex = 0, isFocused, ariaLabel }, ref) => (
  <StyledNode
    nodeType="course"
    onClick={onClick}
    onKeyDown={onKeyDown}
    tabIndex={tabIndex}
    isFocused={isFocused}
    role="button"
    ref={ref}
    aria-label={ariaLabel || `Course: ${course.title}${course.code ? ', Code: ' + course.code : ''}`}
  >
    <div>
      <div className="d-flex align-items-center justify-content-center">
        <i className="ti ti-book-2 node-icon" aria-hidden="true"></i>
        <span className="node-title">{course.title}</span>
      </div>
      {course.code && <div className="node-subtitle">Code: {course.code}</div>}
    </div>
  </StyledNode>
));

const SubjectNode = React.forwardRef<
  HTMLDivElement,
  {
    subject: any;
    onClick?: () => void;
    onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
    tabIndex?: number;
    isFocused?: boolean;
    ariaLabel?: string;
  }
>(({ subject, onClick, onKeyDown, tabIndex = 0, isFocused, ariaLabel }, ref) => (
  <StyledNode
    nodeType="subject"
    onClick={onClick}
    onKeyDown={onKeyDown}
    tabIndex={tabIndex}
    isFocused={isFocused}
    role="button"
    ref={ref}
    aria-label={ariaLabel || `Subject: ${subject.title}${subject.code ? ', Code: ' + subject.code : ''}`}
  >
    <div>
      <div className="d-flex align-items-center justify-content-center">
        <i className="ti ti-notebook node-icon" aria-hidden="true"></i>
        <span className="node-title">{subject.title}</span>
      </div>
      {subject.code && <div className="node-subtitle">Code: {subject.code}</div>}
    </div>
  </StyledNode>
));

const SyllabusNode = React.forwardRef<
  HTMLDivElement,
  {
    syllabus: any;
    onClick?: () => void;
    onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
    tabIndex?: number;
    isFocused?: boolean;
    ariaLabel?: string;
  }
>(({ syllabus, onClick, onKeyDown, tabIndex = 0, isFocused, ariaLabel }, ref) => (
  <StyledNode
    nodeType="syllabus"
    onClick={onClick}
    onKeyDown={onKeyDown}
    tabIndex={tabIndex}
    isFocused={isFocused}
    role="button"
    ref={ref}
    aria-label={ariaLabel || `Syllabus: ${syllabus.title}${syllabus.code ? ', Code: ' + syllabus.code : ''}`}
  >
    <div>
      <div className="d-flex align-items-center justify-content-center">
        <i className="ti ti-file-text node-icon" aria-hidden="true"></i>
        <span className="node-title">{syllabus.title}</span>
      </div>
      {syllabus.code && <div className="node-subtitle">Code: {syllabus.code}</div>}
    </div>
  </StyledNode>
));

const RelationshipVisualization: React.FC<RelationshipVisualizationProps> = ({
  curriculumId,
  courseId,
  showFullHierarchy = false,
  data
}) => {
  // State for the loaded data
  const [curricula, setCurricula] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isLoading, callFetch } = useFetch();

  // State for keyboard navigation
  const [focusedNode, setFocusedNode] = useState<{ type: string; id: number } | null>(null);
  const nodesRef = useRef<Map<string, HTMLElement>>(new Map());

  // Flattened structure for keyboard navigation
  const [flattenedNodes, setFlattenedNodes] = useState<Array<{ type: string; id: number; parentId?: number; name: string }>>([]);

  // Effect to load data or use provided data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Using callFetch from useFetch hook
        const responseData = await callFetch(
          API.ACADEMIC_LENS,
          'GET',
          getAPIHeaders(),
          null,
          null
        );
        
        let allData = responseData;
        
        if (curriculumId) {
          const filteredData = allData.filter((curriculum: any) => curriculum.id === curriculumId);
          setCurricula(filteredData);
        } else {
          setCurricula(allData);
        }

        // Create flattened node structure for keyboard navigation
        if (allData && allData.length > 0) {
          const flatNodes: Array<{ type: string; id: number; parentId?: number; name: string }> = [];

          allData.forEach((curriculum: any) => {
            flatNodes.push({ type: 'curriculum', id: curriculum.id, name: curriculum.title });

            curriculum.courses?.forEach((course: any) => {
              flatNodes.push({ type: 'course', id: course.id, parentId: curriculum.id, name: course.title });

              course.subjects?.forEach((subject: any) => {
                flatNodes.push({ type: 'subject', id: subject.id, parentId: course.id, name: subject.title });

                if (subject.syllabus) {
                  flatNodes.push({
                    type: 'syllabus',
                    id: subject.syllabus.id,
                    parentId: subject.id,
                    name: subject.syllabus.title
                  });
                }
              });
            });
          });

          setFlattenedNodes(flatNodes);
        }
      } catch (err) {
        setError('Failed to load relationship data. Please try again later.');
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (!data) {
      loadData();
    } else {
      // If data is provided directly as a prop, use it
      setCurricula(Array.isArray(data) ? data : [data]);
      setLoading(false);
      
      // Create flattened node structure for keyboard navigation with provided data
      if (data) {
        const flatNodes: Array<{ type: string; id: number; parentId?: number; name: string }> = [];
        const curricula = Array.isArray(data) ? data : [data];
        
        curricula.forEach((curriculum: any) => {
          flatNodes.push({ type: 'curriculum', id: curriculum.id, name: curriculum.title });

          curriculum.courses?.forEach((course: any) => {
            flatNodes.push({ type: 'course', id: course.id, parentId: curriculum.id, name: course.title });

            course.subjects?.forEach((subject: any) => {
              flatNodes.push({ type: 'subject', id: subject.id, parentId: course.id, name: subject.title });

              if (subject.syllabus) {
                flatNodes.push({
                  type: 'syllabus',
                  id: subject.syllabus.id,
                  parentId: subject.id,
                  name: subject.syllabus.title
                });
              }
            });
          });
        });
        
        setFlattenedNodes(flatNodes);
      }
    }
  }, [data, curriculumId, courseId, showFullHierarchy]); // Removed callFetch from dependencies to avoid infinite loop

  // Function to handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>, node: { type: string; id: number }) => {
    if (!focusedNode) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        // Activate the node (same as clicking)
        e.preventDefault();
        break;

      case 'ArrowUp':
        e.preventDefault();
        navigateToPrevNode();
        break;

      case 'ArrowDown':
        e.preventDefault();
        navigateToNextNode();
        break;

      case 'Home':
        e.preventDefault();
        if (flattenedNodes.length) {
          setFocusedNode({ type: flattenedNodes[0].type, id: flattenedNodes[0].id });
        }
        break;

      case 'End':
        e.preventDefault();
        if (flattenedNodes.length) {
          const lastNode = flattenedNodes[flattenedNodes.length - 1];
          setFocusedNode({ type: lastNode.type, id: lastNode.id });
        }
        break;
    }
  };

  // Navigate to previous node in the flattened structure
  const navigateToPrevNode = () => {
    if (!focusedNode || !flattenedNodes.length) return;

    const currentIndex = flattenedNodes.findIndex(
      node => node.type === focusedNode.type && node.id === focusedNode.id
    );

    if (currentIndex > 0) {
      const prevNode = flattenedNodes[currentIndex - 1];
      setFocusedNode({ type: prevNode.type, id: prevNode.id });
    }
  };

  // Navigate to next node in the flattened structure
  const navigateToNextNode = () => {
    if (!focusedNode || !flattenedNodes.length) return;

    const currentIndex = flattenedNodes.findIndex(
      node => node.type === focusedNode.type && node.id === focusedNode.id
    );

    if (currentIndex < flattenedNodes.length - 1) {
      const nextNode = flattenedNodes[currentIndex + 1];
      setFocusedNode({ type: nextNode.type, id: nextNode.id });
    }
  };

  // Focus the node when focusedNode state changes
  useEffect(() => {
    if (focusedNode) {
      const nodeKey = `${focusedNode.type}-${focusedNode.id}`;
      const element = nodesRef.current.get(nodeKey);
      if (element) {
        element.focus();
      }
    }
  }, [focusedNode]);

  // Register a node ref
  const registerNodeRef = (type: string, id: number, element: HTMLDivElement | null) => {
    if (element) {
      const key = `${type}-${id}`;
      nodesRef.current.set(key, element);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5" aria-live="polite">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading relationship diagram...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert" aria-live="assertive">
        <i className="ti ti-alert-circle me-2" aria-hidden="true"></i>
        {error}
      </div>
    );
  }

  if (!curricula.length) {
    return (
      <EmptyStateMessage aria-live="polite">
        <i className="ti ti-hierarchy-2 d-block" aria-hidden="true"></i>
        <h5>No Data Available</h5>
        <p>There are no curricula, courses, subjects, or syllabi to display.</p>
      </EmptyStateMessage>
    );
  }

  // Filter data based on provided IDs if needed
  let filteredCurricula = curricula;

  if (curriculumId && !showFullHierarchy) {
    filteredCurricula = curricula.filter(curr => curr.id === curriculumId);
  }

  return (
    <RelationshipContainer role="tree" aria-label="Academic relationship hierarchy">
      <div className="mb-4 text-center">
        <div className="legend d-flex justify-content-center gap-3 flex-wrap" aria-label="Legend" role="group">
          <div className="legend-item d-flex align-items-center">
            <StyledNode nodeType="curriculum" style={{ width: '20px', height: '20px', minWidth: 'auto' }} aria-hidden="true"></StyledNode>
            <span className="ms-2 small">Curriculum</span>
          </div>
          <div className="legend-item d-flex align-items-center">
            <StyledNode nodeType="course" style={{ width: '20px', height: '20px', minWidth: 'auto' }} aria-hidden="true"></StyledNode>
            <span className="ms-2 small">Course</span>
          </div>
          <div className="legend-item d-flex align-items-center">
            <StyledNode nodeType="subject" style={{ width: '20px', height: '20px', minWidth: 'auto' }} aria-hidden="true"></StyledNode>
            <span className="ms-2 small">Subject</span>
          </div>
          <div className="legend-item d-flex align-items-center">
            <StyledNode nodeType="syllabus" style={{ width: '20px', height: '20px', minWidth: 'auto' }} aria-hidden="true"></StyledNode>
            <span className="ms-2 small">Syllabus</span>
          </div>
        </div>
      </div>

      {/* Accessibility instructions */}
      <div className="visually-hidden" aria-live="polite">
        Use arrow keys to navigate between nodes. Press Enter or Space to select a node.
      </div>

      {filteredCurricula.map((curriculum, index) => (
        <div 
          key={curriculum.id} 
          role="treeitem"
          className="curriculum-container mb-5"
          style={{
            padding: '20px',
            background: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
            borderRadius: '10px',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            position: 'relative'
          }}
        >
          {filteredCurricula.length > 1 && (
            <div 
              style={{
                position: 'absolute',
                top: '-12px',
                left: '20px',
                background: '#f8f9fa',
                padding: '0 10px',
                borderRadius: '4px',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#666'
              }}
            >
              Curriculum {index + 1}
            </div>
          )}
          <Tree
            key={curriculum.id}
            lineWidth={'2px'}
            lineColor={'#ccc'}
            lineBorderRadius={'10px'}
            label={
              <CurriculumNode
                curriculum={curriculum}
                onKeyDown={e => handleKeyDown(e, { type: 'curriculum', id: curriculum.id })}
                isFocused={focusedNode?.type === 'curriculum' && focusedNode?.id === curriculum.id}
                ref={el => registerNodeRef('curriculum', curriculum.id, el)}
                ariaLabel={`Curriculum: ${curriculum.title}. Use arrow keys to navigate.`}
              />
            }
          >
            {curriculum.courses?.map((course: any) => (
              <TreeNode
                key={course.id}
                label={
                  <CourseNode
                    course={course}
                    onKeyDown={e => handleKeyDown(e, { type: 'course', id: course.id })}
                    isFocused={focusedNode?.type === 'course' && focusedNode?.id === course.id}
                    ref={el => registerNodeRef('course', course.id, el)}
                    ariaLabel={`Course: ${course.title} under curriculum ${curriculum.title}`}
                  />
                }
              >
                {course.subjects?.map((subject: any) => (
                  <TreeNode
                    key={subject.id}
                    label={
                      <SubjectNode
                        subject={subject}
                        onKeyDown={e => handleKeyDown(e, { type: 'subject', id: subject.id })}
                        isFocused={focusedNode?.type === 'subject' && focusedNode?.id === subject.id}
                        ref={el => registerNodeRef('subject', subject.id, el)}
                        ariaLabel={`Subject: ${subject.title} under course ${course.title}`}
                      />
                    }
                  >
                    {subject.syllabus && (
                      <TreeNode
                        label={
                          <SyllabusNode
                            syllabus={subject.syllabus}
                            onKeyDown={e => handleKeyDown(e, { type: 'syllabus', id: subject.syllabus.id })}
                            isFocused={focusedNode?.type === 'syllabus' && focusedNode?.id === subject.syllabus.id}
                            ref={el => registerNodeRef('syllabus', subject.syllabus.id, el)}
                            ariaLabel={`Syllabus: ${subject.syllabus.title} for subject ${subject.title}`}
                          />
                        }
                      />
                    )}
                  </TreeNode>
                ))}
              </TreeNode>
            ))}
          </Tree>
        </div>
      ))}
    </RelationshipContainer>
  );
};

export default RelationshipVisualization;